import { useState, useEffect, createContext, useContext, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import TransactionsPage from "./pages/TransactionsPage";
import SalesPage from "./pages/SalesPage";
import SettingsPage from "./pages/SettingsPage";
import PersonalFinancePage from "./pages/PersonalFinancePage";
import AccountExpensesPage from "./pages/AccountExpensesPage";
import SupplierDashboardPage from "./pages/SupplierDashboardPage";
import MerchDashboardPage from "./pages/MerchDashboardPage";
import MerchExpensesPage from "./pages/MerchExpensesPage";
import MerchCostsPage from "./pages/MerchCostsPage";
import InventoryPage from "./pages/InventoryPage";
import MerchProductsPage from "./pages/MerchProductsPage";
import CredentialsPage from "./pages/CredentialsPage";
import BranchesPage from "./pages/BranchesPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Error Boundary to catch React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', backgroundColor: '#FEE2E2', minHeight: '100vh' }}>
          <h2 style={{ color: '#DC2626' }}>Algo salió mal</h2>
          <p>Por favor recarga la página o contacta soporte.</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ padding: '10px 20px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '5px', marginTop: '10px' }}
          >
            Recargar página
          </button>
          <details style={{ marginTop: '20px', whiteSpace: 'pre-wrap' }}>
            <summary>Detalles del error (para soporte)</summary>
            <p>{this.state.error && this.state.error.toString()}</p>
            <p>{this.state.errorInfo && this.state.errorInfo.componentStack}</p>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Theme Context - Dark mode support
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
    // Apply theme class to root element
    if (isDarkMode) {
      document.documentElement.classList.add("dark-mode");
    } else {
      document.documentElement.classList.remove("dark-mode");
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Date Filter Context - Global date range selection
const DateFilterContext = createContext(null);

export const useDateFilter = () => {
  const context = useContext(DateFilterContext);
  if (!context) {
    throw new Error("useDateFilter must be used within DateFilterProvider");
  }
  return context;
};

// Helper function to get date ranges
const getDateRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  // Helper: last day of a given month/year
  const endOfMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0);

  switch (preset) {
    case 'today':
      return { startDate: today, endDate: today, label: 'Hoy' };
    case 'yesterday':
      return { startDate: yesterday, endDate: yesterday, label: 'Ayer' };
    case 'last7days':
      const last7 = new Date(today);
      last7.setDate(last7.getDate() - 6);
      return { startDate: last7, endDate: today, label: 'Últimos 7 días' };
    case 'last30days':
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 29);
      return { startDate: last30, endDate: today, label: 'Últimos 30 días' };
    case 'thisMonth': {
      // Include ALL of the current month (so future-dated expenses within the month appear)
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const thisMonthEnd = endOfMonth(today.getFullYear(), today.getMonth());
      return { startDate: thisMonthStart, endDate: thisMonthEnd, label: 'Este Mes' };
    }
    case 'lastMonth':
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: lastMonthStart, endDate: lastMonthEnd, label: 'Mes Pasado' };
    case 'last3months': {
      // Rolling window: from 3 months ago (start of that month) to END of current month
      const last3Start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      const last3End = endOfMonth(today.getFullYear(), today.getMonth());
      return { startDate: last3Start, endDate: last3End, label: 'Últimos 3 meses' };
    }
    case 'last6months': {
      const last6Start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      const last6End = endOfMonth(today.getFullYear(), today.getMonth());
      return { startDate: last6Start, endDate: last6End, label: 'Últimos 6 meses' };
    }
    case 'thisYear': {
      const thisYearStart = new Date(today.getFullYear(), 0, 1);
      const thisYearEnd = new Date(today.getFullYear(), 11, 31);
      return { startDate: thisYearStart, endDate: thisYearEnd, label: 'Este Año' };
    }
    case 'lastYear':
      const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
      return { startDate: lastYearStart, endDate: lastYearEnd, label: 'Año Pasado' };
    default: {
      const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const defaultEnd = endOfMonth(today.getFullYear(), today.getMonth());
      return { startDate: defaultStart, endDate: defaultEnd, label: 'Este Mes' };
    }
  }
};

// Date Filter Provider
const DateFilterProvider = ({ children }) => {
  const defaultRange = getDateRange('thisMonth');
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [selectedPreset, setSelectedPreset] = useState('thisMonth');
  const [presetLabel, setPresetLabel] = useState(defaultRange.label);

  const applyPreset = (preset) => {
    const range = getDateRange(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setSelectedPreset(preset);
    setPresetLabel(range.label);
  };

  const applyCustomRange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setSelectedPreset('custom');
    setPresetLabel('Personalizado');
  };

  return (
    <DateFilterContext.Provider value={{ 
      startDate, 
      endDate, 
      selectedPreset,
      presetLabel,
      applyPreset, 
      applyCustomRange,
      getDateRange
    }}>
      {children}
    </DateFilterContext.Provider>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Inactivity timeout - 24 hours in milliseconds
  const INACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000;
  const LAST_ACTIVITY_KEY = "lastActivityTime";

  // Update last activity time
  const updateLastActivity = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  };

  // Check if session has expired due to inactivity
  const checkInactivity = () => {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      if (elapsed > INACTIVITY_TIMEOUT) {
        return true; // Session expired
      }
    }
    return false;
  };

  // Set up activity listeners
  useEffect(() => {
    if (token && user) {
      // Update activity on user interactions
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
      
      const handleActivity = () => {
        updateLastActivity();
      };

      events.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
      });

      // Initial activity update
      updateLastActivity();

      // Check inactivity every minute
      const inactivityCheck = setInterval(() => {
        if (checkInactivity()) {
          console.log("Session expired due to inactivity (24 hours)");
          logout();
          window.location.href = '/login';
        }
      }, 60000); // Check every minute

      return () => {
        events.forEach(event => {
          window.removeEventListener(event, handleActivity);
        });
        clearInterval(inactivityCheck);
      };
    }
  }, [token, user]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        // Check if session expired due to inactivity before validating token
        if (checkInactivity()) {
          console.log("Session expired due to inactivity on load");
          localStorage.removeItem("token");
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setToken(null);
          setLoading(false);
          return;
        }

        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          updateLastActivity();
        } catch (error) {
          localStorage.removeItem("token");
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.access_token);
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("token", response.data.access_token);
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF5F5] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Protected Route for Admin/Manager only
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF5F5] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Suppliers cannot access admin/manager pages
  if (user.role === 'proveedor') {
    return <Navigate to="/supplier" replace />;
  }

  return children;
};

// Protected Route for Merch section (Admin + Gerente Merch only)
const MerchRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF5F5] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Only admin and gerente_merch can access Merch pages
  if (!['admin', 'gerente_merch'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Route wrapper for /merch/inventory that ALSO allows gerentes heladería with can_edit_inventory
const InventoryRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF5F5] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const role = user.role;
  const allowed =
    role === 'admin' ||
    role === 'gerente_merch' ||
    ((role === 'gerente_heladeria' || role === 'manager') && !!user.can_edit_inventory);

  if (!allowed) {
    // Fallback: heladería sin permiso → dashboard heladería; merch → dashboard merch
    return <Navigate to={role === 'gerente_merch' ? '/merch' : '/'} replace />;
  }

  return children;
};

// Protected Route for Heladeria section (Admin + Gerente Heladeria only)
const HeladeriaRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF5F5] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Gerente Merch cannot access Heladeria pages
  if (user.role === 'gerente_merch') {
    return <Navigate to="/merch" replace />;
  }

  // Suppliers cannot access
  if (user.role === 'proveedor') {
    return <Navigate to="/supplier" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <DateFilterProvider>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#FFFFFF',
                border: '1px solid #FECDD3',
                color: '#4A4A4A',
              },
            }}
          />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Supplier Dashboard - also accessible via /user-view for admin */}
            <Route path="/supplier" element={
              <ProtectedRoute>
                <SupplierDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/user-view" element={
              <ProtectedRoute>
                <SupplierDashboardPage />
              </ProtectedRoute>
            } />
            {/* Admin/Manager Routes */}
            <Route path="/" element={
              <HeladeriaRoute>
                <DashboardPage />
              </HeladeriaRoute>
            } />
            <Route path="/transactions" element={
              <HeladeriaRoute>
                <TransactionsPage />
              </HeladeriaRoute>
            } />
            <Route path="/sales" element={
              <HeladeriaRoute>
                <SalesPage />
              </HeladeriaRoute>
            } />
            <Route path="/settings" element={
              <AdminRoute>
                <SettingsPage />
              </AdminRoute>
            } />
            <Route path="/personal-finance" element={
              <AdminRoute>
                <PersonalFinancePage />
              </AdminRoute>
            } />
            <Route path="/account-expenses" element={
              <AdminRoute>
                <AccountExpensesPage />
              </AdminRoute>
            } />
            {/* Merch Routes - Admin + Gerente Merch only */}
            <Route path="/merch" element={
              <MerchRoute>
                <MerchDashboardPage />
              </MerchRoute>
            } />
            <Route path="/merch/dashboard" element={
              <MerchRoute>
                <MerchDashboardPage />
              </MerchRoute>
            } />
            <Route path="/merch/expenses" element={
              <MerchRoute>
                <MerchExpensesPage />
              </MerchRoute>
            } />
            <Route path="/merch/costs" element={
              <MerchRoute>
                <MerchCostsPage />
              </MerchRoute>
            } />
            <Route path="/merch/products" element={
              <MerchRoute>
                <MerchProductsPage />
              </MerchRoute>
            } />
            <Route path="/merch/inventory" element={
              <InventoryRoute>
                <InventoryPage />
              </InventoryRoute>
            } />
            <Route path="/credentials" element={
              <AdminRoute>
                <CredentialsPage />
              </AdminRoute>
            } />
            <Route path="/branches" element={
              <AdminRoute>
                <BranchesPage />
              </AdminRoute>
            } />
          </Routes>
        </DateFilterProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
